import React, { ComponentProps, useCallback, useLayoutEffect, useRef, useState } from 'react';
import useMedia from 'use-media';

type Props = {
  fadeDuration?: number;
  heightDuration?: number;
  disabled?: boolean;
} & ComponentProps<'div'>;

// For heights smaller than this, we speed up the height animation. Otherwise it feels too slow for
// a small change.
const HEIGHT_DELTA_THRESHOLD = 100;

export default function AutoFadeTransition({ disabled, children, ...passthroughProps }: Props) {
  const prefersReducedMotion = useMedia('(prefers-reduced-motion: reduce)');
  if (disabled || prefersReducedMotion) {
    return <div {...passthroughProps}>{children}</div>;
  }
  return <AutoFadeTransitionInner {...passthroughProps}>{children}</AutoFadeTransitionInner>;
}

function AutoFadeTransitionInner({
  children,
  fadeDuration = 250,
  heightDuration = 250,
  ...passthroughProps
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [animationState, setAnimationState] = useState<
    | { kind: 'out'; children: React.ReactNode }
    | { kind: 'outFinished'; prevChildrenHeight: number; children: React.ReactNode }
    | { kind: 'in'; children: React.ReactNode }
  >();

  // Indicates that another animation needs to happen after the current one has finished. This
  // happens when children change while an animation is already in progress.
  const enqueuedAnimationRef = useRef<React.ReactNode>();

  const startAnimation = useCallback(
    (prevChildren: React.ReactNode, nextChildren: React.ReactNode) => {
      const contentEl = contentRef.current;
      if (!contentEl) {
        return;
      }
      const animation = contentEl.animate([{ opacity: 1 }, { opacity: 0 }], {
        // Speed up fade out if there's no old content
        duration: !prevChildren ? 1 : fadeDuration,
        easing: 'ease-out',
        fill: 'forwards',
      });
      setAnimationState({
        kind: 'out',
        children: prevChildren,
      });
      animation.addEventListener('finish', () => {
        setAnimationState({
          kind: 'outFinished',
          prevChildrenHeight: contentEl.offsetHeight,
          children: nextChildren,
        });
      });
    },
    [fadeDuration],
  );

  useLayoutEffect(() => {
    if (!containerRef.current || !contentRef.current || animationState?.kind !== 'outFinished') {
      return;
    }

    // Speed up fade in if there's no new content
    const adjustedFadeDuration = !animationState.children ? 1 : fadeDuration;

    // Height delta to check if we need to animate height at all.
    const oldHeight = animationState.prevChildrenHeight;
    const newHeight = contentRef.current.offsetHeight;
    const heightDelta = Math.abs(oldHeight - newHeight);

    let opacityAnimation: Animation;
    if (heightDelta > 0) {
      const adjustedHeightDuration =
        heightDuration * Math.min(1, heightDelta / HEIGHT_DELTA_THRESHOLD);
      containerRef.current.animate([{ height: `${oldHeight}px` }, { height: `${newHeight}px` }], {
        duration: adjustedHeightDuration,
        easing: 'ease-in-out',
      });
      opacityAnimation = contentRef.current.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: adjustedFadeDuration,
        delay: adjustedHeightDuration,
        easing: 'ease-in',
        fill: 'forwards',
      });
    } else {
      opacityAnimation = contentRef.current.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: adjustedFadeDuration,
        easing: 'ease-in',
        fill: 'forwards',
      });
    }

    setAnimationState({ kind: 'in', children: animationState.children });
    opacityAnimation.addEventListener('finish', () => {
      setAnimationState(undefined);
      if (enqueuedAnimationRef.current) {
        // Skip enqueued animation if the old and the new element are identical. This avoids
        // animating from an element to itself.
        if (elementDiffer(animationState.children, enqueuedAnimationRef.current)) {
          startAnimation(animationState.children, enqueuedAnimationRef.current);
        }
        enqueuedAnimationRef.current = undefined;
      }
    });
  }, [animationState, fadeDuration, heightDuration, startAnimation]);

  // Keep track of previous children so we can animate after children change.
  const prevChildrenRef = useRef(children);
  const prevChildren = prevChildrenRef.current;
  prevChildrenRef.current = children;

  // Check if we need too start the animation, after the children have changed between renders. We
  // need to do that during render, so that we can continue to render the prevChildren in the
  // render. Else we'd briefly render the new children until the animationState has been set, which
  // would reset component state for the prevChildren once we render them again after animation has
  // started. Note that we can't call startAnimation during the render, as it is not allowed to have
  // a setState call during render. Instead we start the animation in a layout effect immediately
  // after render.
  const shouldStartAnimationRef = useRef<{
    prevChildren: React.ReactNode;
    nextChildren: React.ReactNode;
  }>();
  if (elementDiffer(prevChildren, children)) {
    // We're currently animating, enqueue a fade to the new content once we're done. Note that older
    // enqueued state is simply overwritten, we just need to show the newest one.
    if (animationState) {
      enqueuedAnimationRef.current = children;
    } else {
      shouldStartAnimationRef.current = { prevChildren, nextChildren: children };
    }
  }

  useLayoutEffect(() => {
    if (shouldStartAnimationRef.current) {
      startAnimation(
        shouldStartAnimationRef.current.prevChildren,
        shouldStartAnimationRef.current.nextChildren,
      );
      shouldStartAnimationRef.current = undefined;
    }
  });

  return (
    <div ref={containerRef} {...passthroughProps}>
      <div ref={contentRef}>
        {animationState ?
          animationState.children
        : shouldStartAnimationRef.current ?
          shouldStartAnimationRef.current.prevChildren
        : children}
      </div>
    </div>
  );
}

function elementDiffer(prevChildren: React.ReactNode, children: React.ReactNode) {
  if (prevChildren === children) {
    return false;
  }

  if (!prevChildren && !children) {
    return false;
  }

  if (!prevChildren && children) {
    return true;
  }

  if (prevChildren && !children) {
    return true;
  }

  if (
    typeof prevChildren === 'object' &&
    prevChildren !== null &&
    'key' in prevChildren &&
    typeof children === 'object' &&
    children !== null &&
    'key' in children &&
    prevChildren.key === children.key &&
    prevChildren.type === children.type
  ) {
    return false;
  }

  return true;
}
